import {
        fetchCurrentUser,
        forgotPassword,
        login,
        logout,
        passwordRecovery,
        register,
        updateProfile,
        emailVerification,
        updatePassword
} from '@/api/auth'
import { locale, syncUserLocale, t } from '@/i18n'

export default {
        namespaced: true,
        state: {
                user: null,
                isAuthenticated: !!localStorage.getItem('isAuthenticated'),
                loginSuccess: false,
                forgotPasswordSuccess: false,
                passwordRecoverySuccess: false,
                emailVerificationSuccess: false,
                registrationSuccess: false,
        },
        getters: {
                isAuthenticated(state) {
                        return state.isAuthenticated
                },
                loginSuccess(state) {
                        return state.loginSuccess
                },
                registrationSuccess(state) {
                        return state.registrationSuccess
                },
        },
        mutations: {
                SET_USER(state, user) {
                        state.user = user
                        state.isAuthenticated = !!user
                        syncUserLocale(user)

                        if (user) {
                                localStorage.setItem(
                                        'user',
                                        JSON.stringify(user)
                                )
                                localStorage.setItem('isAuthenticated', 'true')
                        } else {
                                localStorage.removeItem('user')
                                localStorage.removeItem('isAuthenticated')
                                localStorage.removeItem('userToken')
                        }
                },
                CLEAR_USER(state) {
                        state.user = null
                        state.isAuthenticated = false
                        syncUserLocale(null)
                        localStorage.removeItem('user')
                        localStorage.removeItem('isAuthenticated')
                        localStorage.removeItem('userToken')
                        localStorage.removeItem('pendingPlan')
                        localStorage.removeItem('pendingPlanPrice')
                },
                SET_REGISTRATION_SUCCESS(state, data) {
                        state.registrationSuccess = data.success
                        if (data.success) {
                                localStorage.setItem('isAuthenticated', true)
                                localStorage.setItem('userToken', data.result.accessToken)
                        }
                },
                SET_LOGIN_SUCCESS(state, data) {
                        state.loginSuccess = data.success
                        if (data.success) {
                                localStorage.setItem('isAuthenticated', true)
                                localStorage.setItem('userToken', data.result.accessToken)
                        }
                },
                SET_EMAIL_VERIFICATION_SUCCESS(state, success) {
                        state.emailVerificationSuccess = success
                },
                SET_FORGOT_PASSWORD_SUCCESS(state, success) {
                        state.forgotPasswordSuccess = success
                },
                SET_PASSWORD_RECOVERY_SUCCESS(state, success) {
                        state.passwordRecoverySuccess = success
                },
        },
        actions: {
                async login({ commit, dispatch }, credentials) {
                        try {
                                const response = await login(credentials)
                                if (response.success) {
                                        commit('SET_USER', response.result.user)
                                        commit('SET_LOGIN_SUCCESS', response)
                                        await dispatch('subscription/clear', null, { root: true })
                                        await dispatch('subscription/init', null, { root: true })
                                }
                                return response
                        } catch (error) {
                                commit('SET_LOGIN_SUCCESS', { success: false })

                                if (error.response && error.response.data) {
                                        return error.response.data
                                }

                                return {
                                        success: false,
                                        errors: {
                                                title: t('auth.loginError'),
                                                details: {}
                                        }
                                }
                        }
                },
                async logout({ commit }) {
                        await logout()
                        commit('CLEAR_USER')
                },

                async loadUser({ commit, dispatch }) {
                        try {
                                const response = await fetchCurrentUser()
                                if (response.success && response.result.user) {
                                        commit('SET_USER', response.result.user)
                                        await dispatch('ui/initAvatarSrcFromUser', null, { root: true })
                                        await dispatch('subscription/init', null, { root: true })
                                }
                                return response
                        } catch (error) {
                                if (error.response?.status === 401) {
                                        commit('CLEAR_USER')
                                }
                                throw error
                        }
                },

                async updateProfile({ commit }, { profileData, onUploadProgress = null }) {
                        try {
                                const response = await updateProfile(profileData, onUploadProgress)

                                if (response.success && response.result?.user) {
                                        commit('SET_USER', response.result.user)
                                }

                                return response
                        } catch (error) {
                                if (error.response && error.response.data) {
                                        return error.response.data
                                }

                                return {
                                        success: false,
                                        errors: {
                                                title: t('auth.profileUpdateError'),
                                                details: {}
                                        }
                                }
                        }
                },
                async register({ commit, dispatch }, formData) {
                        try {
                                const backendData = {
                                        email: formData.email,
                                        password: formData.password,
                                        fullname: formData.fullname || null,
                                        username: formData.username,
                                        phone: formData.phone || null,
                                        promoCode: formData.promoCode || null,
                                        language: locale.value
                                }
                                const response = await register(backendData)
                                if (response.success) {
                                        commit('SET_USER', response.result.user)
                                        await dispatch('subscription/clear', null, { root: true })
                                        await dispatch('subscription/init', null, { root: true })
                                }
                                commit('SET_REGISTRATION_SUCCESS', response)
                                return response
                        } catch (error) {
                                commit('SET_REGISTRATION_SUCCESS', { success: false })

                                if (error.response && error.response.data) {
                                        return error.response.data
                                }

                                return {
                                        success: false,
                                        errors: {
                                                title: t('auth.registerError'),
                                                details: {}
                                        }
                                }
                        }
                },
                async passwordRecovery({ commit }, formData) {
                        try {
                                const response = await passwordRecovery(formData)
                                commit('SET_PASSWORD_RECOVERY_SUCCESS', response.success)
                                return response
                        } catch (error) {
                                commit('SET_PASSWORD_RECOVERY_SUCCESS', false)
                                if (error.response && error.response.data) {
                                        return error.response.data
                                }
                                return {
                                        result: {
                                                success: false,
                                                message: t('auth.somethingWrong'),
                                        },
                                }
                        }
                },
                async emailVerification({ commit }, formData) {
                        try {
                                const response = await emailVerification(formData)
                                commit('SET_EMAIL_VERIFICATION_SUCCESS', response.success)
                                return response
                        } catch (error) {
                                commit('SET_EMAIL_VERIFICATION_SUCCESS', false)
                                return {
                                        result: {
                                                success: false,
                                                message: t('auth.somethingWrong'),
                                        },
                                }
                        }
                },
                async forgotPassword({ commit }, formData) {
                        try {
                                const response = await forgotPassword(formData)
                                commit('SET_FORGOT_PASSWORD_SUCCESS', response.success)
                                return response
                        } catch (error) {
                                commit('SET_FORGOT_PASSWORD_SUCCESS', false)
                                if (error.response && error.response.data) {
                                        return error.response.data
                                }
                                return {
                                        success: false,
                                        errors: {
                                                title: t('auth.somethingWrong'),
                                                details: {}
                                        }
                                }
                        }
                },
                async updatePassword({ commit }, formData) {
                        try {
                                const response = await updatePassword(formData);
                                commit('SET_UPDATE_PASSWORD_SUCCESS', response.success)
                                return response;
                        } catch (error) {
                                commit('SET_UPDATE_PASSWORD_SUCCESS', false)
                                return {
                                        result: {
                                                success: false,
                                                message: error.message,
                                        },
                                }
                        }
                }
        },
}
